import React, { useState, useEffect } from 'react';
import HomePage from './pages/Home';
import OnboardingPage from './pages/Onboarding';
import DestinationPlanPage from './pages/DestinationPlan';
import ResultsPage from './pages/Results';
import TripDetailsPage from './pages/TripDetails';
import BudgetResultsPage from './pages/BudgetResults';
import SurpriseResultsPage from './pages/SurpriseResultsNew';
import PricingPage from './pages/Pricing';
import SuccessPage from './pages/Success';
import LoadingScreen from './components/LoadingScreen';
import SplashScreen from './components/SplashScreen';
import { UserProvider } from './context/UserContext';
import { CurrencyProvider } from './context/CurrencyContext';
import { Toaster } from './components/ui/sonner';
import './App.css';

export default function App() {
  const [showSplash, setShowSplash] = useState(true);
  const [currentPage, setCurrentPage] = useState('home');
  const [userProfile, setUserProfile] = useState(null);
  const [tripRequest, setTripRequest] = useState(null);
  const [tripResults, setTripResults] = useState(null);
  const [selectedTrip, setSelectedTrip] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [budgetAmount, setBudgetAmount] = useState(null);
  const [isSurpriseMode, setIsSurpriseMode] = useState(false);

  // Handle URL-based routing for Stripe redirects
  useEffect(() => {
    const path = window.location.pathname;
    const searchParams = new URLSearchParams(window.location.search);
    
    if (path === '/success' && searchParams.get('session_id')) {
      setShowSplash(false);
      setCurrentPage('success');
    } else if (path === '/pricing') {
      setShowSplash(false);
      setCurrentPage('pricing');
    }
  }, []);

  const handleSplashComplete = () => {
    setShowSplash(false);
  };

  const handleStartPlanning = (mode) => {
    if (mode === 'destination') {
      setCurrentPage('onboarding');
    }
  };

  const handleBudgetSearch = (budget) => {
    setBudgetAmount(budget);
    setIsSurpriseMode(false);
    setCurrentPage('budget-results');
  };

  const handleSurpriseMe = (budget) => {
    setBudgetAmount(budget);
    setIsSurpriseMode(true);
    setCurrentPage('surprise-results');
  };

  const handleSurpriseTripSelect = (trip) => {
    // Convert surprise trip from new API format to full trip format for details page
    const durationDays = trip.durationDays || parseInt(trip.duration) || 4;
    
    const fullTrip = {
      ...trip,
      id: trip.id,
      title: `${trip.duration} in ${trip.destination}`,
      destination: trip.destination,
      country: trip.country,
      continent: 'Asia',
      cities: [trip.destination],
      swoopyMatch: trip.vibePercent,
      totalBudgetMin: trip.cost,
      dailyBudgetMin: Math.round(trip.cost / durationDays),
      currency: 'USD',
      whyMatches: [
        `A ${trip.vibe.toLowerCase()} ${trip.pace.toLowerCase()} getaway`,
        `Comfortably under your $${budgetAmount} budget`,
        `${trip.activityFocus} focus with ${trip.foodFocus.toLowerCase()} food`
      ],
      tags: trip.tags,
      details: generateMockTripDetails({
        ...trip,
        duration: trip.duration
      })
    };
    setSelectedTrip(fullTrip);
    setCurrentPage('trip-details');
  };

  const handleBudgetTripSelect = (trip) => {
    // Convert budget trip to full trip format for details page
    const fullTrip = {
      ...trip,
      id: trip.id,
      title: `${trip.duration} in ${trip.destination}`,
      destination: trip.destination,
      country: trip.country,
      continent: 'Asia',
      cities: [trip.destination],
      swoopyMatch: trip.vibePercent,
      totalBudgetMin: trip.cost,
      dailyBudgetMin: Math.round(trip.cost / parseInt(trip.duration)),
      currency: trip.currency || 'USD',
      whyMatches: [
        `Perfect for your ${trip.vibe.toLowerCase()} travel style`,
        `Within your budget of $${budgetAmount}`,
        `Great ${trip.tags[0].toLowerCase()} experiences`
      ],
      tags: trip.tags,
      details: generateMockTripDetails(trip)
    };
    setSelectedTrip(fullTrip);
    setCurrentPage('trip-details');
  };

  const handleOnboardingComplete = (profile) => {
    setUserProfile(profile);
    setCurrentPage('destination-plan');
  };

  const handleSkipOnboarding = () => {
    // Set default profile
    setUserProfile({
      name: 'Traveler',
      travelGroup: 'solo',
      travelStyle: 'mid-range',
      interests: ['city', 'culture', 'food'],
      currency: 'IDR'
    });
    setCurrentPage('destination-plan');
  };

  const handleGeneratePlan = async (request) => {
    setTripRequest(request);
    setIsLoading(true);
    
    // Simulate API call with loading screen
    const results = await generateTripRecommendations(request, userProfile);
    
    setTripResults(results);
    setIsLoading(false);
    setCurrentPage('results');
  };

  const handleViewTripDetails = (trip) => {
    setSelectedTrip(trip);
    setCurrentPage('trip-details');
  };

  const handleBackToResults = () => {
    // Navigate back to appropriate results page
    if (isSurpriseMode) {
      setCurrentPage('surprise-results');
    } else if (budgetAmount) {
      setCurrentPage('budget-results');
    } else {
      setCurrentPage('results');
    }
    setSelectedTrip(null);
  };

  const handleBackToHome = () => {
    setCurrentPage('home');
    setUserProfile(null);
    setTripRequest(null);
    setTripResults(null);
    setSelectedTrip(null);
    // Update URL to root
    window.history.pushState({}, '', '/');
  };

  const handleNavigateToPricing = () => {
    setCurrentPage('pricing');
    window.history.pushState({}, '', '/pricing');
  };

  // Show splash screen on initial load
  if (showSplash) {
    return <SplashScreen onComplete={handleSplashComplete} />;
  }

  // Show loading screen
  if (isLoading) {
    return <LoadingScreen message="Finding your Swoopy Picks..." />;
  }

  return (
    <UserProvider>
    <CurrencyProvider>
      <div className="App min-h-screen bg-background">
        <Toaster position="top-center" />
        
        {currentPage === 'home' && (
          <HomePage 
            onStartPlanning={handleStartPlanning} 
            onSurpriseMe={handleSurpriseMe}
          />
        )}
        
        {currentPage === 'budget-results' && (
          <BudgetResultsPage 
            budget={budgetAmount}
            currency="USD"
            onSelectTrip={handleBudgetTripSelect}
            onBack={handleBackToHome}
          />
        )}
        
        {currentPage === 'surprise-results' && (
          <SurpriseResultsPage 
            budget={budgetAmount}
            onSelectTrip={handleSurpriseTripSelect}
            onBack={handleBackToHome}
          />
        )}
        
        {currentPage === 'onboarding' && (
          <OnboardingPage 
            onComplete={handleOnboardingComplete}
            onSkip={handleSkipOnboarding}
          />
        )}
        
        {currentPage === 'destination-plan' && (
          <DestinationPlanPage 
            userProfile={userProfile}
            onGeneratePlan={handleGeneratePlan}
            onBack={handleBackToHome}
          />
        )}
        
        {currentPage === 'results' && (
          <ResultsPage 
            userProfile={userProfile}
            tripRequest={tripRequest}
            results={tripResults}
            onViewDetails={handleViewTripDetails}
            onBack={() => setCurrentPage('destination-plan')}
          />
        )}
        
        {currentPage === 'trip-details' && (
          <TripDetailsPage 
            trip={selectedTrip}
            userProfile={userProfile}
            onBack={handleBackToResults}
          />
        )}

        {currentPage === 'pricing' && (
          <PricingPage 
            onBack={handleBackToHome}
          />
        )}

        {currentPage === 'success' && (
          <SuccessPage 
            onContinue={handleBackToHome}
          />
        )}
      </div>
    </CurrencyProvider>
    </UserProvider>
  );
}

// Trip recommendation generator (mock AI logic)
async function generateTripRecommendations(request, profile) {
  // Simulate API delay for loading screen
  await new Promise(resolve => setTimeout(resolve, 2000));
  
  const { destination, duration, travelers, budget, includeFlights } = request;
  const { travelStyle, interests, currency } = profile;
  
  // Generate 6 trip recommendations based on destination and preferences
  const trips = generateTripsForDestination(destination, duration, travelers, travelStyle, interests, budget, currency);
  
  return {
    destination,
    trips,
    meta: {
      generatedAt: new Date().toISOString(),
      currency
    }
  };
}

function generateTripsForDestination(destination, duration, travelers, style, interests, budget, currency) {
  // This would normally call an AI API, but for MVP we'll use smart logic
  const trips = [];
  
  const destinationLower = destination.toLowerCase();
  const cityData = getCityData(destinationLower);
  
  // Generate 6 variations based on interests
  const tripTypes = generateTripTypes(interests, cityData);
  
  tripTypes.slice(0, 6).forEach((tripType, index) => {
    const baseCost = calculateBaseCost(cityData, style, duration, travelers);
    const minBudget = Math.round(baseCost * 0.85);
    const comfortBudget = Math.round(baseCost * 1.15);
    
    // Calculate Swoopy Match score
    const swoopyMatch = calculateSwoopyMatch(tripType, interests, style, duration, budget, minBudget, comfortBudget);
    
    trips.push({
      id: `trip-${index + 1}`,
      title: `${tripType.title} in ${cityData.displayName}`,
      destination: cityData.displayName,
      country: cityData.country,
      continent: cityData.continent,
      cities: cityData.suggestedCities.slice(0, 2),
      duration: duration,
      totalBudgetMin: convertCurrency(minBudget, 'USD', currency),
      totalBudgetComfortable: convertCurrency(comfortBudget, 'USD', currency),
      dailyBudgetMin: convertCurrency(Math.round(minBudget / duration / travelers), 'USD', currency),
      dailyBudgetComfortable: convertCurrency(Math.round(comfortBudget / duration / travelers), 'USD', currency),
      currency,
      whyMatches: tripType.whyMatches,
      tags: tripType.tags,
      vibes: tripType.vibes,
      swoopyMatch: swoopyMatch,
      details: generateTripDetails(cityData, duration, style, tripType, currency, travelers)
    });
  });
  
  // Sort by Swoopy Match score (highest first)
  trips.sort((a, b) => b.swoopyMatch - a.swoopyMatch);
  
  return trips;
}

function calculateSwoopyMatch(tripType, userInterests, userStyle, duration, userBudget, minBudget, comfortBudget) {
  let score = 0;
  let maxScore = 0;
  
  // Interest matching (40 points)
  maxScore += 40;
  const tripTags = tripType.tags.map(t => t.toLowerCase());
  const matchingInterests = userInterests.filter(interest => 
    tripTags.some(tag => tag.includes(interest) || interest.includes(tag.toLowerCase()))
  );
  score += (matchingInterests.length / Math.max(userInterests.length, 1)) * 40;
  
  // Travel style match (20 points)
  maxScore += 20;
  score += 20; // Assume all trips match the style since we filter by it
  
  // Duration fit (20 points)
  maxScore += 20;
  if (duration >= 3 && duration <= 10) {
    score += 20; // Ideal duration
  } else if (duration < 3 || duration > 14) {
    score += 10; // Short or long trips
  } else {
    score += 15; // Medium length
  }
  
  // Budget fit (20 points) - only if budget provided
  if (userBudget && userBudget > 0) {
    maxScore += 20;
    if (userBudget >= minBudget && userBudget <= comfortBudget * 1.2) {
      score += 20; // Perfect budget fit
    } else if (userBudget >= minBudget * 0.8) {
      score += 15; // Close to budget
    } else {
      score += 5; // Budget mismatch
    }
  }
  
  // Convert to percentage
  return Math.round((score / maxScore) * 100);
}

function getCityData(destination) {
  const cityDatabase = {
    'bali': {
      displayName: 'Bali',
      country: 'Indonesia',
      continent: 'Asia',
      suggestedCities: ['Ubud', 'Canggu', 'Seminyak'],
      costMultiplier: 0.7,
      vibes: ['beach', 'wellness', 'culture', 'nature']
    },
    'paris': {
      displayName: 'Paris',
      country: 'France',
      continent: 'Europe',
      suggestedCities: ['Le Marais', 'Montmartre', 'Latin Quarter'],
      costMultiplier: 1.4,
      vibes: ['culture', 'food', 'museums', 'city']
    },
    'tokyo': {
      displayName: 'Tokyo',
      country: 'Japan',
      continent: 'Asia',
      suggestedCities: ['Shibuya', 'Shinjuku', 'Asakusa'],
      costMultiplier: 1.3,
      vibes: ['city', 'food', 'culture', 'shopping']
    },
    'barcelona': {
      displayName: 'Barcelona',
      country: 'Spain',
      continent: 'Europe',
      suggestedCities: ['Gothic Quarter', 'El Born', 'Gracia'],
      costMultiplier: 1.0,
      vibes: ['beach', 'culture', 'food', 'nightlife']
    }
  };
  
  return cityDatabase[destination] || {
    displayName: destination.charAt(0).toUpperCase() + destination.slice(1),
    country: 'Unknown',
    continent: 'Unknown',
    suggestedCities: ['City Center', 'Old Town'],
    costMultiplier: 1.0,
    vibes: ['city']
  };
}

function generateTripTypes(interests, cityData) {
  const types = [];
  
  if (interests.includes('beach') && cityData.vibes.includes('beach')) {
    types.push({
      title: 'Beach & Relaxation Escape',
      whyMatches: [
        'Beach activities match your interests',
        'Perfect for unwinding and water sports',
        'Great weather year-round'
      ],
      tags: ['Beach', 'Relaxation', 'Water Sports'],
      vibes: ['Chill', 'Beach']
    });
  }
  
  if (interests.includes('food')) {
    types.push({
      title: 'Foodie Adventure',
      whyMatches: [
        'World-class culinary scene',
        'Local markets and street food',
        'Food tours and cooking classes available'
      ],
      tags: ['Food', 'Culture', 'Local Experience'],
      vibes: ['Foodie', 'Culture']
    });
  }
  
  if (interests.includes('culture') || interests.includes('museums')) {
    types.push({
      title: 'Cultural Discovery',
      whyMatches: [
        'Rich historical heritage',
        'Museums and galleries',
        'Local traditions and festivals'
      ],
      tags: ['Culture', 'History', 'Museums'],
      vibes: ['Culture', 'Chill']
    });
  }
  
  if (interests.includes('city')) {
    types.push({
      title: 'City Explorer',
      whyMatches: [
        'Urban exploration and landmarks',
        'Great walkability',
        'Diverse neighborhoods to discover'
      ],
      tags: ['City', 'Walking', 'Urban'],
      vibes: ['Energetic', 'City']
    });
  }
  
  if (interests.includes('nightlife')) {
    types.push({
      title: 'Nightlife & Entertainment',
      whyMatches: [
        'Vibrant nightlife scene',
        'Bars and clubs in every neighborhood',
        'Live music and events'
      ],
      tags: ['Nightlife', 'Entertainment', 'Social'],
      vibes: ['Energetic', 'Party']
    });
  }
  
  // Add generic option
  types.push({
    title: 'Balanced City Break',
    whyMatches: [
      'Mix of activities for all interests',
      'Well-connected and easy to navigate',
      'Something for everyone'
    ],
    tags: ['Balanced', 'Flexible', 'All-rounder'],
    vibes: ['Chill', 'Balanced']
  });
  
  return types;
}

function calculateBaseCost(cityData, style, duration, travelers) {
  const baseDaily = {
    'budget': 50,
    'mid-range': 120,
    'luxury': 300
  };
  
  const dailyCost = baseDaily[style] * cityData.costMultiplier;
  return dailyCost * duration * travelers;
}

function convertCurrency(amount, from, to) {
  const rates = {
    'USD': 1,
    'EUR': 0.92,
    'GBP': 0.79,
    'JPY': 149.5,
    'SGD': 1.34,
    'MYR': 4.47,
    'THB': 35.25,
    'AUD': 1.52,
    'IDR': 15850,
    'TRY': 32.15
  };
  
  const usdAmount = from === 'USD' ? amount : amount / rates[from];
  return Math.round(usdAmount * rates[to]);
}

function generateTripDetails(cityData, duration, style, tripType, currency, travelers) {
  // Generate detailed trip information
  return {
    budgetBreakdown: generateBudgetBreakdown(cityData, duration, style, currency, travelers),
    whereToStay: generateWhereToStay(cityData, style),
    whereToEat: generateWhereToEat(cityData, style),
    miniItinerary: generateMiniItinerary(cityData, duration, tripType),
    smartTips: generateSmartTips(cityData, style)
  };
}

function generateBudgetBreakdown(cityData, duration, style, currency, travelers) {
  const dailyRates = {
    'budget': { accommodation: 25, food: 15, transport: 10, activities: 10 },
    'mid-range': { accommodation: 70, food: 35, transport: 20, activities: 30 },
    'luxury': { accommodation: 200, food: 80, transport: 50, activities: 70 }
  };
  
  const rates = dailyRates[style];
  const multiplier = cityData.costMultiplier;
  
  const accommodation = convertCurrency(Math.round(rates.accommodation * multiplier * duration), 'USD', currency);
  const food = convertCurrency(Math.round(rates.food * multiplier * duration * travelers), 'USD', currency);
  const transport = convertCurrency(Math.round(rates.transport * multiplier * duration), 'USD', currency);
  const activities = convertCurrency(Math.round(rates.activities * multiplier * duration * travelers), 'USD', currency);
  const buffer = Math.round((accommodation + food + transport + activities) * 0.1);
  
  return {
    accommodation: { perNight: Math.round(accommodation / duration), total: accommodation },
    food: { perDay: Math.round(food / duration / travelers), total: food },
    transport: { total: transport },
    activities: { total: activities },
    buffer: { percentage: 10, total: buffer },
    grandTotal: accommodation + food + transport + activities + buffer
  };
}

function generateWhereToStay(cityData, style) {
  const neighborhoods = cityData.suggestedCities.map((area, index) => ({
    name: area,
    bestFor: index === 0 ? 'Central location, walkable' : index === 1 ? 'Local vibe, affordable' : 'Quiet, residential',
    accommodationTypes: style === 'budget' ? ['Hostels', 'Budget hotels', 'Guesthouses'] : 
                        style === 'luxury' ? ['Boutique hotels', '4-5 star hotels', 'Luxury apartments'] :
                        ['3-star hotels', 'Airbnb', 'Mid-range hotels'],
    searchPhrases: [
      `best ${style} hotel in ${area} ${cityData.displayName}`,
      `${area} ${cityData.displayName} accommodation`,
      `where to stay ${area}`
    ]
  }));
  
  return { neighborhoods };
}

function generateWhereToEat(cityData, style) {
  return {
    localDishes: ['Local specialty dishes', 'Street food favorites', 'Regional cuisine'],
    foodAreas: cityData.suggestedCities.map(city => `${city} food scene`),
    searchPhrases: [
      `cheap eats ${cityData.displayName}`,
      `best local food ${cityData.displayName}`,
      `food markets ${cityData.displayName}`,
      `restaurants in ${cityData.suggestedCities[0]}`
    ]
  };
}

function generateMiniItinerary(cityData, duration, tripType) {
  const days = [];
  for (let i = 1; i <= Math.min(duration, 7); i++) {
    days.push({
      day: i,
      plan: i === 1 ? `Arrive in ${cityData.displayName}, settle in, explore nearby area` :
            i === duration ? `Last day - visit remaining spots, departure prep` :
            `Explore ${cityData.suggestedCities[(i - 1) % cityData.suggestedCities.length]}, ${tripType.tags[i % tripType.tags.length].toLowerCase()} activities`
    });
  }
  return days;
}

function generateSmartTips(cityData, style) {
  return [
    `Best time to visit ${cityData.displayName} is during shoulder season for fewer crowds`,
    `Public transport is the most budget-friendly way to get around`,
    `Book accommodations at least 2-3 weeks in advance for better rates`,
    `Local markets offer authentic food at great prices`,
    `Consider purchasing a city pass for museums and attractions`
  ];
}

// Generate mock trip details for budget trips
function generateMockTripDetails(trip) {
  const durationDays = parseInt(trip.duration) || 4;
  
  return {
    budgetBreakdown: {
      accommodation: { perNight: Math.round(trip.cost * 0.35 / durationDays), total: Math.round(trip.cost * 0.35) },
      food: { perDay: Math.round(trip.cost * 0.25 / durationDays), total: Math.round(trip.cost * 0.25) },
      transport: { total: Math.round(trip.cost * 0.15) },
      activities: { total: Math.round(trip.cost * 0.20) },
      buffer: { percentage: 5, total: Math.round(trip.cost * 0.05) },
      grandTotal: trip.cost
    },
    whereToStay: {
      neighborhoods: [
        { name: 'City Center', bestFor: 'Central location, walkable', accommodationTypes: ['Hotels', 'Hostels', 'Airbnb'] },
        { name: 'Local Area', bestFor: 'Authentic experience, affordable', accommodationTypes: ['Guesthouses', 'Budget hotels'] }
      ]
    },
    whereToEat: {
      localDishes: ['Local specialty dishes', 'Street food favorites'],
      foodAreas: ['Food street', 'Night market'],
      searchPhrases: [`best food ${trip.destination}`, `cheap eats ${trip.destination}`]
    },
    miniItinerary: Array.from({ length: Math.min(durationDays, 5) }, (_, i) => ({
      day: i + 1,
      plan: i === 0 ? `Arrive in ${trip.destination}, explore nearby` : 
            i === durationDays - 1 ? 'Last day - final sightseeing, departure' :
            `Explore ${trip.tags[i % trip.tags.length]} activities`
    })),
    smartTips: [
      `Book accommodations early for better rates in ${trip.destination}`,
      'Use local transport to save money',
      'Visit local markets for authentic food'
    ]
  };
}
